import {Injectable} from '@angular/core';
import {EditorRoutingService} from '@management/editor/editor-routing.service';
import {
  Block,
  BlockType,
  Column,
  DateInput,
  FileUploadInput,
  Input,
  Partial,
  RadioInput,
  RadioInputFormat,
  Row,
  SignatureInput,
  TextInput
} from '@paperlessio/sdk/api/models';
import {BlockSerializer} from '@paperlessio/sdk/api/serializers';
import {BlockMutationCollection, BlockMutationResult} from '../block/block-mutation.type';
import {BlockStore} from '../block/block.store';
import {DropZone} from './drop-zone/drop-zone';
import {BehaviorSubject} from 'rxjs';
import {BlockChangeDetectionService} from '@blocks/services/block-change-detection/block-change-detection.service';
import {PartialStore} from '@management/partials/partial.store';
import {Point} from '@angular/cdk/drag-drop';

@Injectable()
export class DraggableService {
  isDragging$ = new BehaviorSubject(false);
  duplicate = false;
  block: Block | null = null;
  partial: Partial | null = null;
  partialChildren: Block[] | null = null;
  relatedBlock: Block | null = null;
  zone: DropZone | null = null;
  position: number | null = null;
  absolutePosition: Point | null = null;
  collection: BlockMutationCollection | null = null;

  constructor(
    private blockStore: BlockStore,
    private partialStore: PartialStore,
    private editorRoutingService: EditorRoutingService,
    private blockChangeDetectionService: BlockChangeDetectionService
  ) { }

  public apply() {
    if (!this.relatedBlock || this.relatedBlock.id === this.block?.id || !this.zone) {
      return this.reset();
    }

    this.buildMutationCollection();
    this.send();
    this.reset();
  }

  public buildMutationCollection() {
    const relatedBlock = this.relatedBlock;

    this.collection = new BlockMutationCollection(relatedBlock.bucket_type, relatedBlock.bucket_id);

    switch (this.zone) {
      case DropZone.Above:
      case DropZone.Below:
      case DropZone.Inside:
        this.addOrMoveOrUpdateBlock(relatedBlock, this.position);
        this.blockChangeDetectionService.setChangedBlockIds([this.block.parent?.id, relatedBlock.id]);
        break;
      case DropZone.After:
      case DropZone.Before:
        this.relatedBlock.type === BlockType.Column ? this.addColumn() : this.addRowAndColumns();
        this.blockChangeDetectionService.setChangedBlockIds([this.block.parent?.id, relatedBlock.parent?.id]);
        break;
    }
  }

  private addOrMoveOrUpdateBlock(parent: Block, position: number) {
    if (parent.type === BlockType.PdfPart) {
      // When a block-button from the sidebar is dragged onto the editor, the settings are null.
      (this.block.settings as any) ??= {};
      this.block.settings.absolutePosition = this.absolutePosition;
    }

    if (this.block.id && !this.duplicate && parent.type !== BlockType.PdfPart) {
      this.moveBlock(this.block, parent, position);
    } else if (this.block.id && !this.duplicate && parent.type === BlockType.PdfPart) {
      this.moveBlock(this.block, parent, position); // always move to top
      this.collection.updateBlock(this.block);
    } else {
      if (parent.type === BlockType.PdfPart) {
        if (this.block.type.includes('::Input::')) {
          (this.block as Input).settings.hide_name = true;
        }

        if (this.block.type === BlockType.TextInput) {
          (this.block as TextInput).settings.multiline = true;
          (this.block as TextInput).settings.fontSize = 14;
          (this.block as Input).settings.required = true;
        }

        if (this.block.type === BlockType.DateInput) {
          (this.block as TextInput).settings.fontSize = 14;
          (this.block as Input).settings.required = true;
          (this.block as DateInput).settings.today = false;
        }

        if (this.block.type === BlockType.SignatureInput) {
          (this.block as SignatureInput).settings.show_location = false;
          (this.block as SignatureInput).settings.show_date = false;
          (this.block as Input).settings.required = true;
        }

        if (this.block.type === BlockType.RadioInput) {
          (this.block.styles as any) ??= {};
          (this.block as RadioInput).styles.format = RadioInputFormat.DROPDOWN;
          (this.block as TextInput).settings.fontSize = 14;
        }

        if (this.block.type === BlockType.FileUploadInput || this.block.type === BlockType.SecureFileUploadInput) {
          (this.block as FileUploadInput).settings.maxFiles = 1;
          (this.block as FileUploadInput).settings.fontSize = 14;
          (this.block as Input).settings.required = true;
        }
      }

      this.addBlock(parent, position);
    }
  }

  private addBlock(parent: Block, position: number): BlockMutationCollection {
    // A block of type group indicates that a partial is being dropped
    if (this.partial != null) {
      this.partialStore.get(this.block.id);
      return this.blockStore.duplicatePartialContent(this.collection, this.partialChildren, this.partial, parent, position);
    } else if (this.duplicate) {
      return this.collection.duplicateBlock(this.block, parent, position);
    } else {
      return this.collection.createBlock(this.block = new BlockSerializer().fromJson(this.block), parent, position);
    }
  }

  private moveBlock(block: Block, parent: Block, position: number): BlockMutationCollection {
    // TODO: Check if this is really needed.
    block = this.blockStore.getSync(block.id) || block;
    return this.collection.moveBlock(block, position, parent);
  }

  private addColumn() {
    const row = this.relatedBlock.parent;
    const column = new Column({type: BlockType.Column});
    const zone = this.zone === DropZone.Before ? this.position : this.position + 1;

    this.collection.createBlock(column, row, zone);
    this.addOrMoveOrUpdateBlock(column, 0);
  }

  private addRowAndColumns() {
    const relatedBlock = this.relatedBlock;
    const row = new Row({type: BlockType.Row});

    this.collection.createBlock(row, relatedBlock.parent, this.position);

    const columns = row.children as Column[];

    if (this.zone === DropZone.Before) {
      this.addOrMoveOrUpdateBlock(columns[0], 0);
      this.moveBlock(relatedBlock, columns[1], 0);

      return;
    }

    this.moveBlock(relatedBlock, columns[0], 0);
    this.addOrMoveOrUpdateBlock(columns[1], 0);
  }

  private send() {
    if (!this.collection.mutations.length) {
      return;
    }

    const block = this.block;
    this.blockStore
      .sendAndUpdate(this.collection)
      .subscribe((blockMutationResult: BlockMutationResult) => {
        const createdBlock = blockMutationResult.created_blocks.find(cb => cb.local_uuid === block.local_uuid);
        if (createdBlock) {
          this.editorRoutingService.navigateToBlock(createdBlock.id);
        }
      });
  }

  private reset() {
    this.isDragging$.next(this.duplicate = false);
    this.partial = null;
    this.partialChildren = null;
    this.block = null;
    this.relatedBlock = null;
    this.position = null;
    this.collection = null;
    this.absolutePosition = null;
    this.zone = DropZone.None;
  }
}
